import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { mdiRobotOutline } from '@mdi/js'
import type { AgentMission, AgentRun } from '../../api/schema'
import { Can, useCan } from '../../contexts/PermissionsContext'
import { useAssetsByUids } from '../../hooks/useQueries'
import { useLocale } from '../../contexts/LocaleContext'
import { AppIcon } from '../ui/icon'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import {
    missionsFor,
    runAction,
    useAgentMissions,
    useAgentRun,
    useAgentRunLive,
    useAgentRuns,
    useInvalidateAgentRuns,
    type MissionTarget,
    type TargetEntityType,
} from './agent-runs-api'
import { AGENT_RUN_STATUS, displayStatus, toneVariant } from './agent-run-status'
import { AgentRunCard } from './AgentRunCard'
import { AgentRunBanner } from './AgentRunBanner'
import { LaunchAgentRunDialog } from './LaunchAgentRunDialog'
import { outputFloorUids } from './AgentRunOutputs'
import { useCanOpenRunDetails } from './can-open-run'

/** Names of the floors a run's outputs mention. */
export function useFloorNames(org: string, run: AgentRun | undefined): Record<string, string> {
    const uids = outputFloorUids(run?.output)
    const { data } = useAssetsByUids(org, uids)
    const names: Record<string, string> = {}
    for (const a of data ?? []) if (a?.uid) names[a.uid] = a.name ?? a.uid
    return names
}

/** Wires one run into the card: live refresh, decisions, floor names. */
export function LiveAgentRunCard({
    org,
    runUid,
    mission,
    floorAssetUid,
}: {
    org: string
    runUid: string
    mission?: AgentMission
    floorAssetUid?: string
}) {
    const { data: run, refetch } = useAgentRun(org, runUid)
    const canDecide = useCan('data.write')
    const canOpenDetails = useCanOpenRunDetails()
    const invalidate = useInvalidateAgentRuns(org)
    const floorNames = useFloorNames(org, run)
    const { t } = useTranslation('agent-runs')
    useAgentRunLive(org, run, refetch)
    if (!run) return null

    return (
        <AgentRunCard
            org={org}
            run={run}
            mission={mission}
            canDecide={canDecide}
            canOpenDetails={canOpenDetails}
            floorAssetUid={floorAssetUid}
            floorNames={floorNames}
            onDecision={async (decision) => {
                const err = await runAction(org, run.uid, decision)
                await invalidate()
                if (!err) return undefined
                return err.title || t('card.decision_failed')
            }}
        />
    )
}

/** The compact twin of LiveAgentRunCard: one row, same live refresh and decisions. */
export function LiveAgentRunBanner({
    org,
    runUid,
    mission,
    onRelaunch,
}: {
    org: string
    runUid: string
    mission?: AgentMission
    onRelaunch?: () => void
}) {
    const { data: run, refetch } = useAgentRun(org, runUid)
    const canDecide = useCan('data.write')
    const canOpenDetails = useCanOpenRunDetails()
    const invalidate = useInvalidateAgentRuns(org)
    const { t } = useTranslation('agent-runs')
    useAgentRunLive(org, run, refetch)
    if (!run) return null

    return (
        <AgentRunBanner
            org={org}
            run={run}
            mission={mission}
            canDecide={canDecide}
            canOpenDetails={canOpenDetails}
            onRelaunch={onRelaunch}
            onDecision={async (decision) => {
                const err = await runAction(org, run.uid, decision)
                await invalidate()
                if (!err) return undefined
                return err.title || t('card.decision_failed')
            }}
        />
    )
}

/**
 * The agent runs of one target (spec 2026-10-21-19 §2): the launch button, the
 * latest run as a card, earlier runs as rows. Renders nothing when no mission
 * offers a pair for the target and no run exists.
 */
export function AgentRunsPanel({
    org,
    targetEntityType,
    targetUid,
    missionTarget,
    compact = false,
}: {
    org: string
    targetEntityType: TargetEntityType
    targetUid: string
    missionTarget: MissionTarget
    /** One-line banner (the site page): no earlier runs, the launch folds into the row. */
    compact?: boolean
}) {
    const { t } = useTranslation('agent-runs')
    const { locale } = useLocale()
    const [open, setOpen] = useState(false)
    const { data: missionsData } = useAgentMissions(org)
    const { data: runsData } = useAgentRuns(org, targetEntityType, targetUid)
    const invalidate = useInvalidateAgentRuns(org)
    const canOpenDetails = useCanOpenRunDetails()

    const all = missionsData?.data ?? []
    const offered = missionsFor(all, missionTarget)
    const runs = runsData?.data ?? []
    if (offered.length === 0 && runs.length === 0) return null

    const [latest, ...earlier] = runs
    const missionOf = (code: string) => all.find((m) => m.code === code)
    const launchLabel = offered.length === 1 ? t(offered[0].title_key, { defaultValue: offered[0].code }) : t('launch.title_many')

    return (
        <div className="space-y-3" data-testid={`agent-runs-panel-${missionTarget}`}>
            {offered.length > 0 && !(compact && latest) && (
                <Can perm="data.write">
                    <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="agent-launch-button">
                        <AppIcon path={mdiRobotOutline} size="sm" className="mr-1" />
                        {launchLabel}
                    </Button>
                </Can>
            )}
            {offered.length > 0 && open && (
                <Can perm="data.write">
                    <LaunchAgentRunDialog
                        org={org}
                        open={open}
                        onOpenChange={setOpen}
                        missions={offered}
                        writeMode={missionsData?.write_mode ?? 'propose'}
                        targetEntityType={targetEntityType}
                        targetUid={targetUid}
                        onLaunched={() => void invalidate()}
                    />
                </Can>
            )}

            {compact && latest && (
                <LiveAgentRunBanner
                    key={latest.uid}
                    org={org}
                    runUid={latest.uid}
                    mission={missionOf(latest.mission_code)}
                    onRelaunch={offered.length > 0 ? () => setOpen(true) : undefined}
                />
            )}

            {!compact && latest && (
                <LiveAgentRunCard
                    key={latest.uid}
                    org={org}
                    runUid={latest.uid}
                    mission={missionOf(latest.mission_code)}
                    floorAssetUid={missionTarget === 'floor' ? targetUid : undefined}
                />
            )}

            {!compact && earlier.length > 0 && (
                <ul className="divide-y divide-border rounded-lg border border-border bg-card text-sm">
                    {earlier.map((run) => {
                        const cfg = AGENT_RUN_STATUS[displayStatus(run)]
                        const m = missionOf(run.mission_code)
                        const title = m ? t(m.title_key, { defaultValue: m.code }) : run.mission_code
                        return (
                            <li key={run.uid} className="flex flex-wrap items-center gap-2 px-3 py-2" data-testid={`agent-run-history-row-${run.uid}`}>
                                {canOpenDetails ? (
                                    <Link to="/$org/agent-runs/$runUid" params={{ org, runUid: run.uid }} className="font-medium text-primary hover:underline">
                                        {title}
                                    </Link>
                                ) : (
                                    <span className="font-medium text-foreground">{title}</span>
                                )}
                                <Badge variant={toneVariant(cfg.tone)}>{t(cfg.labelKey)}</Badge>
                                <span className="ml-auto text-muted-foreground">{new Date(run.created_at).toLocaleString(locale)}</span>
                            </li>
                        )
                    })}
                </ul>
            )}
        </div>
    )
}
